<template>
  <div class="product-event-participants">
    <step-title
      title="Participantes"
      description="Lista de participantes e controle de check-in."
      class="mb-6"
    />

    <action-dispatcher
      ref="overviewDispatcher"
      action="events/getTicketsOverview"
      :parameters="$route.params.id"
    >
      <div class="row mb-6">
        <div class="col-12 col-md mb-6 mb-md-0">
          <div class="ticket-amount">
            <span>Total de ingressos</span>
            <span>{{ ticketsOverview.total_tickets || 0 }}</span>
          </div>
        </div>
        <div class="col-12 col-md mb-6 mb-md-0">
          <div class="ticket-amount">
            <span>Check-in realizados</span>
            <span>{{ ticketsOverview.total_checkin_done || 0 }}</span>
          </div>
        </div>
        <div class="col-12 col-md mb-6 mb-md-0">
          <div class="ticket-amount">
            <span>Check-in pendentes</span>
            <span>{{ ticketsOverview.total_checkin_pending || 0 }}</span>
          </div>
        </div>
        <div class="col-12 col-md">
          <div class="ticket-amount">
            <span>Check-in cancelados</span>
            <span>{{ ticketsOverview.total_checkin_canceled || 0 }}</span>
          </div>
        </div>
      </div>
    </action-dispatcher>

    <div class="row">
      <div class="col-lg-7 d-flex align-items-center order-1 order-lg-0">
        <span class="fs-14 fw-4 text-neutral-800 mr-3">
          Total: {{ count }}
        </span>
        <button class="btn p-0 btn-sm" @click="refreshPage">
          <i class="fa-light fa-refresh" />
        </button>
      </div>
      <div class="col-lg-5 d-flex flex-column flex-lg-row justify-content-end">
        <div class="d-flex align-items-center flex-fill">
          <bp-input
            size="md"
            v-model="filters.search"
            placeholder="Buscar por nome ou e-mail"
            @change="refresh"
            :delay="300"
          />

          <bp-filter-button
            description
            button-class="btn btn-new-secondary btn-40 px-0 btn-lg-32 ml-3"
          >
            <template #default="{ close }">
              <event-participants-filter
                :filters="filters"
                @close="close"
                @apply="applyFilters"
              />
            </template>
          </bp-filter-button>

          <button
            class="btn btn-new-secondary btn-40 px-0 btn-lg-32 ml-3"
            @click="exportParticipants"
            :disabled="exportLoading"
          >
            <loading :show="exportLoading">
              <i class="fa-regular fa-download" />
            </loading>
          </button>
        </div>
        <button
          class="btn btn-primary-500 btn-sm my-5 my-lg-0 ml-lg-3"
          @click="checkIn"
        >
          <i class="fa-regular fa-shield-check mr-3"></i> Fazer check-in
        </button>
      </div>
    </div>

    <action-dispatcher
      ref="participantsDispatcher"
      action="events/getParticipants"
      :parameters="{ product_id: $route.params.id, ...filters }"
    >
      <bp-table-responsive
        empty-message="Você ainda não tem nenhum participante!"
        :pagination-remote-method="onUpdatePagination"
        :pagination="pagination"
        :columns="columns"
        :frSize="size"
        :data="participants"
        class="mt-6"
      />
    </action-dispatcher>

    <responsive-modal v-model="checkin_visible">
      <event-checkin-modal
        :participant="participant_opened"
        @success="onCheckinSuccess"
        @repeated-code="onRepeatedCode"
        @close="checkin_visible = false"
      />
    </responsive-modal>

    <responsive-modal v-model="participant_detail_visible" size="lg">
      <participant-detail-modal
        :data="participant_opened"
        @close="participant_detail_visible = false"
      />
    </responsive-modal>

    <bp-dialog
      v-model="dialog.value"
      v-bind="dialogOptions"
      @action="onDialogAction"
    />
  </div>
</template>
<script>
import EventCheckinModal from "@/components/events/EventCheckinModal.vue";
import EventParticipantsFilter from "@/components/product/events/EventParticipantsFilter.vue";
import ParticipantDetailModal from "@/components/product/events/ParticipantDetailModal.vue";

import moment from "moment";
import { mapState } from "vuex";
export default {
  components: {
    EventParticipantsFilter,
    ParticipantDetailModal,
    EventCheckinModal
  },
  data() {
    return {
      checkin_visible: false,
      participant_opened: null,
      participant_detail_visible: false,
      loadingOverview: false,
      filters: {
        search: "",
        checkin_done: ""
      },
      dialog: {
        value: false,
        variant: "positive",
        is_manual: false
      },
      exportLoading: false
    };
  },
  computed: {
    ...mapState("events", ["participants", "pagination", "ticketsOverview"]),
    ...mapState("product", ["product"]),
    count() {
      return this.pagination.count || 0;
    },
    columns() {
      return [
        {
          name: `Código do pedido`,
          default: "-",
          field: "order_id",
          transform: v => `BP${v}`
        },
        {
          name: "Comprador",
          default: "-",
          field: "shopper_email",
          transform: (_, item) =>
            `<p class="mb-0 pb-0">
              <span class="fs-14 fw-4 text-neutral-500">${item.shopper_name}</span>
              <br/>
              <span class="fs-12 fw-4 text-neutral-400">${item.shopper_email}</span>
            </p>`
        },
        { name: "Ingresso", default: "-", field: "lot_name" },
        {
          name: "Data do check-in",
          default: "-",
          field: "checkin_at",
          transform: v => (v ? new Date(v).toLocaleDateString() : "-")
        },
        {
          name: "Check-in",
          default: "-",
          field: "checkin_at",
          transform: (_, item) => `
            <span
              class="${this.formatStatusBadge(item)}"
            >
              ${this.formatStatusName(item)}
            </span>`
        },
        {
          name: "",
          type: "menu",
          options: item => [
            ...(!item.checkin_at
              ? [
                  {
                    label: "Fazer check-in manualmente",
                    icon: "fa-light fa-circle-check",
                    class: "",
                    click: item => {
                      this.dialog.is_manual = true;
                      this.participant_opened = item;
                      this.checkin_visible = true;
                    }
                  }
                ]
              : []),
            {
              label: "Detalhes",
              icon: "fa-light fa-info-circle",
              class: "",
              click: item => {
                this.participant_opened = item;
                this.participant_detail_visible = true;
              }
            }
          ]
        }
      ];
    },
    size() {
      return {
        desktop: "1fr 1.5fr 1fr 1fr 0.4fr 0.3fr ",
        mobile: "2fr 1fr 0.5fr"
      };
    },
    dialogOptions() {
      const variant = this.dialog.variant;
      return {
        variant,
        text:
          variant == "positive"
            ? "Check-in realizado com sucesso!"
            : "Esse código já foi utilizado.",
        buttonText: this.dialog.is_manual
          ? "Ok, entendi"
          : "Fazer novo check-in",
        icon:
          variant == "positive"
            ? "fa fa-circle-check"
            : "fa fa-circle-exclamation"
      };
    }
  },
  methods: {
    formatStatusName(participant) {
      if (participant.status == "canceled") return "Cancelado";
      return participant.checkin_at ? "Realizado" : "Pendente";
    },
    formatStatusBadge(participant) {
      if (participant.status == "canceled") return "inactive-badge";
      return participant.checkin_at ? "active-badge" : "draft-badge";
    },

    applyFilters(filters) {
      this.filters = { ...this.filters, ...filters };
      requestIdleCallback(this.refresh);
    },
    refresh() {
      this.$refs.participantsDispatcher.dispatch();
    },
    refreshPage() {
      this.$refs.overviewDispatcher.dispatch();
      this.$refs.participantsDispatcher.dispatch();
    },
    onUpdatePagination(offset) {
      this.$store
        .dispatch("events/getParticipants", {
          product_id: this.$route.params.id,
          ...this.filters,
          offset
        })
        .then();
    },
    getOverview() {
      this.loadingOverview = true;
      this.$store
        .dispatch("events/getTicketsOverview", this.$route.params.id)
        .finally(() => (this.loadingOverview = false));
    },
    checkIn() {
      if (this.participant_opened) this.participant_opened = null;
      if (this.dialog.is_manual) this.dialog.is_manual = false;
      this.checkin_visible = true;
    },
    onCheckinSuccess() {
      this.refreshPage();
      this.checkin_visible = false;
      this.dialog.variant = "positive";
      this.dialog.value = true;
    },
    onRepeatedCode() {
      this.checkin_visible = false;
      this.dialog.variant = "alert";
      this.dialog.value = true;
    },
    onDialogAction() {
      this.dialog.value = false;
      if (!this.dialog.is_manual) this.checkIn();
      setTimeout(() => {
        this.dialog.is_manual = false;
      }, 300);
    },
    async exportParticipants() {
      this.exportLoading = true;
      try {
        const { data } = await this.$store.dispatch(
          "events/exportParticipants"
        );
        var a = document.createElement("a");

        a.href = `data:text/csv;charset=utf-8,${data.replaceAll("#", "%23")}`;

        a.setAttribute(
          "download",
          `event_participants_${moment().format(
            "YYYYMMDD"
          )}_${moment().unix()}.csv`
        );
        a.click();
      } catch (e) {
        this.$message.error("Erro ao exportar dados");
      } finally {
        this.exportLoading = false;
      }
    }
  },
  watch: {
    participant_detail_visible(v) {
      if (!v) {
        setTimeout(() => {
          this.participant_opened = null;
        }, 300);
      }
    }
  },
  mounted() {
    this.getOverview();
  }
};
</script>
